import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Read-only admin audit panel for the active and superseded beta question
 * bank. Surfaces every column added during the premium tiered rollout so
 * admins can audit content quality without opening the database.
 */

interface QRow {
  question_id: string;
  lesson_id: string;
  lesson_name?: string | null;
  question: string;
  option_a: string; option_b: string; option_c: string; option_d: string;
  correct_answer: "A" | "B" | "C" | "D" | string;
  explanation: string | null;
  difficulty: string | null;
  adaptive_tier: string | null;
  concept_tag: string | null;
  misconception_tested: string | null;
  skill_type: string | null;
  adaptive_behavior: string | null;
  is_beta_question: boolean;
  beta_question_version: string | null;
  source_file: string | null;
  active: boolean;
  superseded_at: string | null;
}

type TierFilter = "all" | "easy" | "medium" | "hard" | "expert";
type StatusFilter = "all" | "active" | "inactive";
type FlagFilter =
  | "none"
  | "missing_concept"
  | "missing_explanation"
  | "missing_misconception"
  | "length_bias"
  | "duplicate_text";

function correctOptionLength(q: QRow): number {
  const map: Record<string, string> = { A: q.option_a, B: q.option_b, C: q.option_c, D: q.option_d };
  return (map[q.correct_answer] ?? "").length;
}
function distractorAvg(q: QRow): number {
  const all = ["A", "B", "C", "D"].filter((k) => k !== q.correct_answer);
  const sum = all.reduce((s, k) => s + (((q)[`option_${k.toLowerCase()}`] as string) ?? "").length, 0);
  return sum / Math.max(1, all.length);
}
function hasLengthBias(q: QRow): boolean {
  const c = correctOptionLength(q);
  const a = distractorAvg(q);
  return c > a * 1.6 && c - a > 25;
}

export default function AdminLaunchQuestions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<QRow[]>([]);
  const [tier, setTier] = useState<TierFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("active");
  const [lessonFilter, setLessonFilter] = useState<string>("all");
  const [flag, setFlag] = useState<FlagFilter>("none");
  const [search, setSearch] = useState("");
  const [betaOnly, setBetaOnly] = useState(true);

  const load = async () => {
    setLoading(true);
    let q = supabase.from("questions").select("*").order("lesson_id", { ascending: true });
    if (betaOnly) q = q.eq("is_beta_question", true);
    const { data, error } = await q;
    if (error) { setLoading(false); return; }
    // Hydrate lesson_name in one extra fetch.
    const lessonIds = Array.from(new Set((data ?? []).map((r) => r.lesson_id)));
    const nameMap = new Map<string, string>();
    if (lessonIds.length) {
      const { data: lessons } = await supabase
        .from("lessons")
        .select("lesson_id, lesson_name")
        .in("lesson_id", lessonIds);
      for (const l of (lessons ?? [])) nameMap.set(l.lesson_id, l.lesson_name);
    }
    setRows(((data ?? [])).map((r) => ({ ...r, lesson_name: nameMap.get(r.lesson_id) ?? null })));
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  useEffect(() => { void load(); }, [betaOnly]);

  const lessons = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) if (r.lesson_name) seen.set(r.lesson_id, r.lesson_name);
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  // Duplicate detection: same lesson + identical (case-insensitive) question text.
  const duplicateIds = useMemo(() => {
    const buckets = new Map<string, string[]>();
    for (const r of rows) {
      const key = `${r.lesson_id}::${r.question.trim().toLowerCase()}`;
      const arr = buckets.get(key) ?? [];
      arr.push(r.question_id);
      buckets.set(key, arr);
    }
    const out = new Set<string>();
    for (const arr of buckets.values()) if (arr.length > 1) arr.forEach((id) => out.add(id));
    return out;
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (status === "active" && !r.active) return false;
      if (status === "inactive" && r.active) return false;
      if (tier !== "all" && (r.adaptive_tier ?? "") !== tier) return false;
      if (lessonFilter !== "all" && r.lesson_id !== lessonFilter) return false;
      if (flag === "missing_concept" && r.concept_tag) return false;
      if (flag === "missing_explanation" && r.explanation) return false;
      if (flag === "missing_misconception" && r.misconception_tested) return false;
      if (flag === "length_bias" && !hasLengthBias(r)) return false;
      if (flag === "duplicate_text" && !duplicateIds.has(r.question_id)) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        if (!r.question.toLowerCase().includes(s)
          && !(r.concept_tag ?? "").toLowerCase().includes(s)
          && !(r.lesson_name ?? "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [rows, status, tier, lessonFilter, flag, search, duplicateIds]);

  // Summary numbers
  const stats = useMemo(() => {
    const active = rows.filter((r) => r.active);
    const inactive = rows.filter((r) => !r.active);
    const tiers = { easy: 0, medium: 0, hard: 0, expert: 0 } as Record<string, number>;
    for (const r of active) if (r.adaptive_tier && r.adaptive_tier in tiers) tiers[r.adaptive_tier]++;
    return {
      active: active.length,
      inactive: inactive.length,
      lessonsCovered: new Set(active.map((r) => r.lesson_id)).size,
      lengthBias: active.filter(hasLengthBias).length,
      duplicates: active.filter((r) => duplicateIds.has(r.question_id)).length,
      missingConcept: active.filter((r) => !r.concept_tag).length,
      missingExplanation: active.filter((r) => !r.explanation).length,
      missingMisconception: active.filter((r) => !r.misconception_tested).length,
      tiers,
    };
  }, [rows, duplicateIds]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-7xl px-4 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold">Beta Questions Audit</h1>
            <p className="text-sm text-muted-foreground">
              Read-only inventory of the premium adaptive beta question bank.
            </p>
          </div>
        </div>

        <Card className="p-4">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-8">
            <Stat label="Active" value={stats.active} />
            <Stat label="Inactive" value={stats.inactive} />
            <Stat label="Lessons" value={stats.lessonsCovered} />
            <Stat label="Easy / Med / Hard / Expert" value={`${stats.tiers.easy} / ${stats.tiers.medium} / ${stats.tiers.hard} / ${stats.tiers.expert}`} />
            <Stat label="Length bias" value={stats.lengthBias} highlight={stats.lengthBias > 0} />
            <Stat label="Duplicates" value={stats.duplicates} highlight={stats.duplicates > 0} />
            <Stat label="No concept" value={stats.missingConcept} highlight={stats.missingConcept > 0} />
            <Stat label="No explanation" value={stats.missingExplanation} highlight={stats.missingExplanation > 0} />
          </div>
        </Card>

        <Card className="p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-6">
            <FilterBlock label="Beta only">
              <Select value={betaOnly ? "yes" : "no"} onValueChange={(v) => setBetaOnly(v === "yes")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">All questions</SelectItem>
                </SelectContent>
              </Select>
            </FilterBlock>
            <FilterBlock label="Status">
              <Select value={status} onValueChange={(v: StatusFilter) => setStatus(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active only</SelectItem>
                  <SelectItem value="inactive">Inactive / superseded</SelectItem>
                </SelectContent>
              </Select>
            </FilterBlock>
            <FilterBlock label="Beta question tier">
              <Select value={tier} onValueChange={(v: TierFilter) => setTier(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tiers</SelectItem>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                  <SelectItem value="expert">Expert</SelectItem>
                </SelectContent>
              </Select>
            </FilterBlock>
            <FilterBlock label="Lesson">
              <Select value={lessonFilter} onValueChange={setLessonFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All lessons</SelectItem>
                  {lessons.map(([id, name]) => (
                    <SelectItem key={id} value={id}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterBlock>
            <FilterBlock label="Quality flag">
              <Select value={flag} onValueChange={(v: FlagFilter) => setFlag(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No filter</SelectItem>
                  <SelectItem value="missing_concept">Missing concept tag</SelectItem>
                  <SelectItem value="missing_explanation">Missing explanation</SelectItem>
                  <SelectItem value="missing_misconception">Missing misconception</SelectItem>
                  <SelectItem value="length_bias">Potential length bias</SelectItem>
                  <SelectItem value="duplicate_text">Duplicate question text</SelectItem>
                </SelectContent>
              </Select>
            </FilterBlock>
            <FilterBlock label="Search">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Question, lesson, concept…"
              />
            </FilterBlock>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>{loading ? "Loading…" : `Showing ${filtered.length} of ${rows.length} questions`}</span>
            <Button size="sm" variant="outline" onClick={() => void load()}>Refresh</Button>
          </div>
        </Card>

        <div className="space-y-3">
          {filtered.map((q) => (
            <Card key={q.question_id} className={`p-4 ${q.active ? "" : "opacity-70"}`}>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant={q.active ? "default" : "secondary"}>{q.active ? "Active" : "Superseded"}</Badge>
                {q.adaptive_tier && <Badge variant="outline">{q.adaptive_tier}</Badge>}
                {q.concept_tag && <Badge variant="outline">{q.concept_tag}</Badge>}
                {q.skill_type && <Badge variant="outline">{q.skill_type}</Badge>}
                {q.beta_question_version && <Badge variant="outline">v: {q.beta_question_version}</Badge>}
                {hasLengthBias(q) && <Badge variant="destructive">length bias</Badge>}
                {duplicateIds.has(q.question_id) && <Badge variant="destructive">duplicate</Badge>}
                {!q.concept_tag && <Badge variant="destructive">no concept</Badge>}
                {!q.explanation && <Badge variant="destructive">no explanation</Badge>}
                <span className="ml-auto text-muted-foreground">{q.lesson_name ?? q.lesson_id}</span>
              </div>
              <p className="mt-3 font-heading text-base font-semibold">{q.question}</p>
              <ol className="mt-2 space-y-1 text-sm">
                {(["A","B","C","D"] as const).map((k) => {
                  const isCorrect = q.correct_answer === k;
                  const text = (q)[`option_${k.toLowerCase()}`] as string;
                  return (
                    <li key={k} className={isCorrect ? "text-emerald-300" : "text-foreground/80"}>
                      <span className="font-mono mr-2">{k}.</span>{text}
                      {isCorrect && <span className="ml-2 text-xs">✓ correct</span>}
                    </li>
                  );
                })}
              </ol>
              {q.explanation && (
                <p className="mt-3 text-sm text-muted-foreground"><span className="font-semibold">Explanation:</span> {q.explanation}</p>
              )}
              {q.misconception_tested && (
                <p className="mt-1 text-xs text-muted-foreground"><span className="font-semibold">Misconception tested:</span> {q.misconception_tested}</p>
              )}
              {q.adaptive_behavior && (
                <p className="mt-1 text-xs text-muted-foreground"><span className="font-semibold">Adaptive behavior:</span> {q.adaptive_behavior}</p>
              )}
              <p className="mt-2 text-[10px] text-muted-foreground">
                question_id: {q.question_id} · source: {q.source_file ?? "—"}
                {q.superseded_at ? ` · superseded ${new Date(q.superseded_at).toLocaleDateString()}` : ""}
              </p>
            </Card>
          ))}
          {!loading && filtered.length === 0 && (
            <p className="text-center text-sm text-muted-foreground">No questions match the current filters.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: number | string; highlight?: boolean }) {
  return (
    <div className="rounded-lg border border-border/40 bg-card/60 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 font-heading text-lg font-bold ${highlight ? "text-destructive" : ""}`}>{value}</div>
    </div>
  );
}

function FilterBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}